import {LoggerAppender} from '../logger-appender';
import {LogMessage} from '../../log-message';
import {LogLevel} from '../../log-level';
import {ConfigService} from '../../../config/config-service';
import {inject, Injectable} from '../../../di';
import {SentryBreadcrumbsAppenderConfig} from './sentry-breadcrumbs-appender-config';
import type {SeverityLevel} from '@sentry/core/build/types/types-hoist/severity';
import {filter, map} from 'rxjs';
import {addBreadcrumb} from '@sentry/browser';

const DEFAULT_TRIGGER_LEVEL: LogLevel = LogLevel.Debug;

/**
 * Sentry Breadcrumbs logger appender
 */
@Injectable()
export class SentryBreadcrumbsAppender extends LoggerAppender {
  private readonly configService = inject(ConfigService);

  protected config!: SentryBreadcrumbsAppenderConfig;

  constructor() {
    super();
    this.configService.config$
      .pipe(
        map((c) => c?.logger?.sentryBreadcrumbs),
        filter((c) => !!c),
      )
      .subscribe((c) => this.applyConfig(c));
  }

  private applyConfig(config?: SentryBreadcrumbsAppenderConfig) {
    // validation and fixing config:
    if (!config) {
      // Default config
      config = new SentryBreadcrumbsAppenderConfig();
    }
    if (config.triggerLevel == null) {
      config.triggerLevel = DEFAULT_TRIGGER_LEVEL;
    }

    // applying config
    this.config = config;
  }

  public log(message: LogMessage, level: LogLevel, args: any[], history: LogMessage[]): void {
    if (!this.config.enabled || level < this.config.triggerLevel) {
      return;
    }

    try {
      addBreadcrumb({
        message: message.message,
        type: 'debug',
        category: 'Logger',
        level: LogLevelToSentryLevelMap[level],
        timestamp: message.timestamp.getTime(),
      });
    } catch (error) {
      console.error('[Logger] Unable to add Sentry breadcrumbs', error);
    }
  }
}

/**
 * Map from our local Log Levels to Sentry log levels
 */
const LogLevelToSentryLevelMap: {[logLevel in LogLevel]: SeverityLevel} = {
  [LogLevel.Critical]: 'fatal',
  [LogLevel.Error]: 'error',
  [LogLevel.Warn]: 'warning',
  [LogLevel.Info]: 'log',
  [LogLevel.Debug]: 'debug',
};
